import { collectLegend } from "../core/legend.js";
import { batchDOMUpdate, domCache } from "./dom.js";
import { hideLayer, showLayer } from "./visibility.js";

export function createLegend(config) {
  const {
    containerDivId,
    mapLayers,
    map,
    name,
    layerId,
    layers,
    isAOI,
    fillStyle,
    renderType,
    customFillImage,
    reverseSegments,
  } = config;

  const defaultLegendDiv = "#" + containerDivId + " #legendRow" + layerId;
  const headerDiv = domCache.get(defaultLegendDiv + " .legend-header", true);

  if (!headerDiv) return;

  // Build HTML in memory first, then insert once
  const htmlParts = [];

  if (Array.isArray(layers)) {
    const listStyle = collectLegend(layers);

    if (Array.isArray(listStyle.segments)) {
      htmlParts.push(`<div class="legend-header"><label>${name}</label></div>`);

      const segments = listStyle.segments;
      if (reverseSegments) {
        segments.reverse();
      }
      // Build all segment HTML at once
      const segmentHTML = segments.map(s => {
        const label = s.category ?? s.min + " - " + s.max;
        return `<div class="legend-row subRow">
          <svg class="legend ${listStyle.symbol} size${s.size}" viewBox="0 0 16 16" role="img" aria-label="${label}">
            <rect x="1" y="1" width="14" height="14" fill="${s.color}" />
          </svg>
          <span>${label}</span>
        </div>`;
      }).join('');

      // Batch DOM update
      batchDOMUpdate(() => {
        headerDiv.insertAdjacentHTML('beforeend', htmlParts.join(''));
        domCache.get(defaultLegendDiv, true).insertAdjacentHTML('beforeend', segmentHTML);
      });
    } else if (listStyle.color) {
      const html = `<svg class="legend ${renderType ?? listStyle.symbol}" viewBox="0 0 16 16" role="img" aria-label="${name}">
        <rect x="1" y="1" width="14" height="14" fill="${listStyle.color}" stroke="${listStyle.borderColor}" stroke-width="1" />
        </svg><span>${name}</span>`;

      batchDOMUpdate(() => {
        headerDiv.insertAdjacentHTML('beforeend', html);
      });
    }
  } else {
    const paint = fillStyle ?? collectLegend([layers]);
    const fill = paint[renderType + "-color"] || paint;
    const opacity = ";opacity: " + (fill.opacity ?? 100) / 100;
    const border = paint[renderType + "-outline-color"] || fill;

    const html = `<svg class="legend ${renderType}"
      viewBox="0 0 16 16"
      style="${opacity}"
      role="img"
      aria-label="${name}">
      <rect x="1" y="1" width="14" height="14" fill="${fill}" stroke="${border}" stroke-width="2" />
      </svg><span>${name}</span>`;

    batchDOMUpdate(() => {
      headerDiv.insertAdjacentHTML('beforeend', html);
    });
  }

  // Use event delegation instead of individual listeners
  setupLegendEventDelegation(containerDivId, mapLayers, map);
}

// OPTIMIZED: Event delegation to avoid memory leaks
let eventDelegationSetup = new Set();

export function setupLegendEventDelegation(containerDivId, mapLayers, map) {
  const containerId = containerDivId;

  // Only setup once per container
  if (eventDelegationSetup.has(containerId)) {
    return;
  }

  eventDelegationSetup.add(containerId);

  const container = domCache.get("#" + containerDivId);
  if (!container) return;

  // Single event listener for all inputs using event delegation
  container.addEventListener('change', function(event) {
    const input = event.target;
    if (input.tagName !== 'INPUT' || input.type !== 'checkbox') {
      return;
    }

    const mapId = input.dataset.id;
    const isChecked = input.checked;

    if (mapLayers[mapId]?.linkedLayer) {
      mapLayers[mapId].linkedLayer.forEach((x) => {
        isChecked
          ? showLayer(containerDivId, map, x)
          : hideLayer(containerDivId, map, x);

        const linkedInput = domCache.get("#" + containerDivId + ' *[data-id="' + x + '"]', true);
        if (linkedInput) {
          linkedInput.checked = isChecked;
        }
      });
    } else {
      isChecked
        ? showLayer(containerDivId, map, mapId)
        : hideLayer(containerDivId, map, mapId);
    }
  });

  // Group label toggle (div-based, no <details>/<summary>)
  container.addEventListener('click', function(event) {
    const label = event.target.closest('.legend-group-label');
    if (!label) return;
    const group = label.closest('.legend-group');
    if (!group) return;
    group.classList.toggle('is-collapsed');
  });

  container.addEventListener('keydown', function(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const label = event.target.closest('.legend-group-label');
    if (!label) return;
    const group = label.closest('.legend-group');
    if (!group) return;
    event.preventDefault();
    group.classList.toggle('is-collapsed');
  });
}

export function createLegendRow(containerDivId, layerIds, isAOI, isRadio, isBase, legendGroups = []) {
  if (layerIds.length === 0) {
    return;
  }

  const legendContainer = domCache.get(
    "#" + containerDivId + " #hf-legend .legend-container " +
    (isAOI || isBase ? ".aoi" : " .normal"),
    true
  );

  if (!legendContainer) return;

  // Create all legend rows in a DocumentFragment for batch insertion
  const fragment = document.createDocumentFragment();

  const normalizedLegendGroups = Array.isArray(legendGroups) ? legendGroups : [];

  const normalizeLayerId = (layerId) => String(layerId);
  const normalizeGroupLabel = (label) =>
    String(label || "").replace(/[^a-zA-Z0-9_-]/g, "_");

  const getGroupForLayer = (layerId) => {
    const normalizedLayerId = normalizeLayerId(layerId);
    return normalizedLegendGroups.find((group) =>
      Array.isArray(group?.layers) &&
      group.layers.some((groupLayerId) => normalizeLayerId(groupLayerId) === normalizedLayerId)
    );
  };

  const ensureGroupedContainers = () => {
    let groupsRoot = legendContainer.querySelector(".legend-groups-root");
    if (!groupsRoot && normalizedLegendGroups.length > 0) {
      groupsRoot = document.createElement("div");
      groupsRoot.className = "legend-groups-root";
      legendContainer.appendChild(groupsRoot);
    }

    let ungroupedRoot = legendContainer.querySelector(".legend-ungrouped-root");
    if (!ungroupedRoot) {
      ungroupedRoot = document.createElement("div");
      ungroupedRoot.className = "legend-ungrouped-root";
      legendContainer.appendChild(ungroupedRoot);
    }

    return { groupsRoot, ungroupedRoot };
  };

  const insertGroupContainerInConfigOrder = (groupsRoot, groupContainer, groupLabel) => {
    const currentIndex = normalizedLegendGroups.findIndex(
      (g) => (g?.label || "") === (groupLabel || "")
    );
    if (currentIndex < 0) {
      groupsRoot.appendChild(groupContainer);
      return;
    }

    // Insert before the next existing group in config order.
    let inserted = false;
    for (let idx = currentIndex + 1; idx < normalizedLegendGroups.length; idx++) {
      const nextGroupLabel = normalizedLegendGroups[idx]?.label;
      const nextGroupId = `legendGroup_${normalizeGroupLabel(nextGroupLabel)}`;
      const nextGroupEl = groupsRoot.querySelector(`#${nextGroupId}`);
      if (nextGroupEl) {
        groupsRoot.insertBefore(groupContainer, nextGroupEl);
        inserted = true;
        break;
      }
    }
    if (!inserted) {
      groupsRoot.appendChild(groupContainer);
    }
  };

  layerIds.forEach((id) => {
    const hfLayer = id + (isAOI ? "" : "");

    const div = document.createElement("div");
    const innerDiv = document.createElement("div");
    innerDiv.id = "legendRow" + hfLayer;

    const legendHeader = document.createElement("div");
    legendHeader.className = "legend-header";

    const label = document.createElement("label");
    label.className = "checkbox-label";

    const input = document.createElement("input");
    input.type = "checkbox";
    input.dataset.id = hfLayer;
    input.id = "ch_legend" + hfLayer;
    input.checked = true;
    input.setAttribute("aria-label", `Toggle visibility for layer`);

    const span = document.createElement("span");
    span.className = "checkbox-custom rectangular " +
      (isRadio && !(isAOI || isBase) ? "radio" : "");

    label.appendChild(input);
    label.appendChild(span);
    legendHeader.appendChild(label);
    innerDiv.appendChild(legendHeader);
    div.appendChild(innerDiv);

    const group = getGroupForLayer(id);
    if (group) {
      const { groupsRoot } = ensureGroupedContainers();
      if (!groupsRoot) return;
      const groupId = `legendGroup_${normalizeGroupLabel(group.label)}`;
      let groupContainer = groupsRoot.querySelector(`#${groupId}`);
      if (!groupContainer) {
        groupContainer = document.createElement("div");
        groupContainer.id = groupId;
        groupContainer.className = "legend-group";
        const groupLayerCount = Array.isArray(group?.layers) ? group.layers.length : 0;
        // Collapse larger groups by default to keep the legend compact.
        if (groupLayerCount > 5) {
          groupContainer.classList.add("is-collapsed");
        }

        const labelDiv = document.createElement("div");
        labelDiv.className = "legend-group-label";
        labelDiv.textContent = group.label || "Grouped Layers";
        labelDiv.setAttribute("role", "button");
        labelDiv.setAttribute("tabindex", "0");
        labelDiv.setAttribute("aria-label", `Toggle legend group ${group.label || "Grouped Layers"}`);

        const body = document.createElement("div");
        body.className = "legend-group-items";

        groupContainer.appendChild(labelDiv);
        groupContainer.appendChild(body);
        insertGroupContainerInConfigOrder(groupsRoot, groupContainer, group.label);
      }

      const groupBody = groupContainer.querySelector(".legend-group-items");
      if (groupBody) {
        groupBody.appendChild(div);
      }
    } else {
      const { ungroupedRoot } = ensureGroupedContainers();
      ungroupedRoot.appendChild(div);
    }
  });

  // Single DOM insertion
  batchDOMUpdate(() => {
    if (fragment.childNodes.length > 0) legendContainer.prepend(fragment);
    domCache.remove("#" + containerDivId + " #hf-legend .legend-container");
  });
}
